
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, Users, CalendarCheck, CalendarDays, TrendingUp } from "lucide-react";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { Skeleton } from "@/components/ui/skeleton";

interface StatusCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  isLoading?: boolean;
  trend?: string;
  color: string;
}

const StatusCard: React.FC<StatusCardProps> = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  isLoading, 
  trend,
  color 
}) => (
  <Card className={`border-l-4 ${color} hover:shadow-md transition-shadow dark:bg-white`}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
      <div className={`p-2 rounded-full bg-opacity-10 ${color.replace('border-l-', 'bg-')}`}>
        {icon}
      </div>
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <>
          <Skeleton className="h-8 w-16 mb-1" />
          <Skeleton className="h-4 w-24" />
        </>
      ) : (
        <>
          <div className="flex items-center space-x-2">
            <div className="text-3xl font-bold text-gray-900">{value}</div>
            {trend && (
              <div className="flex items-center text-sm text-green-600">
                <TrendingUp className="h-3 w-3 mr-1" />
                {trend}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </>
      )}
    </CardContent>
  </Card>
);

export const DashboardHeader: React.FC = () => {
  const { profile } = useAuth();
  const { stats, isLoading } = useDashboardStats();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
          Bem-vindo de volta, {profile?.full_name?.split(' ')[0] || 'Usuário'}!
        </h2>
        <p className="text-gray-600">Aqui está um resumo da sua agenda e clientes.</p>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatusCard
          title="Total de Clientes"
          value={stats?.totalClients || 0}
          subtitle="Clientes cadastrados"
          icon={<Users className="h-5 w-5 text-blue-600" />}
          color="border-l-blue-500"
          isLoading={isLoading}
        />
        
        <StatusCard
          title="Hoje"
          value={stats?.todayAppointments || 0}
          subtitle="Compromissos de hoje"
          icon={<Calendar className="h-5 w-5 text-green-600" />}
          color="border-l-green-500"
          isLoading={isLoading}
        />
        
        <StatusCard
          title="Esta Semana"
          value={stats?.weekAppointments || 0}
          subtitle="Compromissos da semana"
          icon={<CalendarDays className="h-5 w-5 text-purple-600" />}
          color="border-l-purple-500"
          isLoading={isLoading}
        />
        
        <StatusCard
          title="Total Ativos"
          value={stats?.totalActiveAppointments || 0}
          subtitle="Compromissos ativos"
          icon={<CalendarCheck className="h-5 w-5 text-orange-600" />}
          color="border-l-orange-500"
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
